// Un viaje de la bandeja (PP-34): estado, reloj y las acciones que la API
// admite en ese estado. La UI deshabilita, la API decide: con el reloj en
// cero los botones se apagan y la bandeja le pregunta al servidor que paso.
//
// Lo que NO muestra, a proposito: a que proveedor paso un viaje, la cola, la
// posicion en ella ni cuantos intentos hubo (PLAN-ASIGNACION-PROVEEDOR §9).
// La fila trae `providerId` y no se pinta.
//
// El resumen (PP-34b) alcanza para decidir sin abrir el recorrido, que es una
// lectura auditada: sentido, horas, paradas y pasajeros, sin personas.
//
// Rediseño (docs/PLAN-REDISENO-PORTAL.md §6.4): la fila tiene la forma de la
// del Inicio (hora, que viaje, plazo o estado, acciones). Rechazar y asignar
// se abren inline; el recorrido y la hoja de ruta, en una hoja lateral.
import { useEffect, useId, useState } from "react";
import type { Driver, OfferRejectReason, ProviderTrip, ProviderTripDetailResponse, SiteVehicle } from "@ucorp/contracts";
import type { PortalSession } from "../auth/session.js";
import { Badge } from "../components/ui/badge.js";
import { Button } from "../components/ui/button.js";
import { cn } from "../components/ui/cn.js";
import { Failure } from "../components/ui/failure.js";
import { Sheet } from "../components/ui/sheet.js";
import { SkeletonRows } from "../components/ui/skeleton.js";
import { EligibilityBadge, SelectField } from "../fleet/parts.js";
import type { RosterState } from "../fleet/useRoster.js";
import { VEHICLE_TYPE_LABEL } from "../logic/fleet.js";
import { formatClock, formatDate, formatRemaining, remainingSeconds, stopNames, summaryParts, tripActions, tripTitle } from "../logic/requests.js";
import { buildRouteSheet } from "../logic/route-sheet.js";
import { DispatchCopy } from "../trips/DispatchCopy.js";
import { AssignForm } from "./AssignForm.js";
import { ASSIGNMENT_STATUS_LABEL, REJECT_REASON_LABEL, REJECT_REASONS, TRIP_STATUS_LABEL } from "./labels.js";
import type { RequestsPort, RequestsSite } from "./port.js";

interface Props {
  trip: ProviderTrip;
  now: number;
  /** Dia que muestra la bandeja: una oferta de otro dia lo dice en la fila. */
  inboxDate: string;
  /** Zona IANA de la planta, para las horas del resumen. */
  timezone: string;
  busy: boolean;
  vehicles: RosterState<SiteVehicle>;
  drivers: RosterState<Driver>;
  session: PortalSession;
  port: RequestsPort;
  site: RequestsSite;
  /** Nombre de la planta de la habilitacion: va en "Copiar datos del viaje" (PP-65). */
  siteName: string;
  /** PP-65: ultimo conductor asignado a cada vehiculo, para proponerlo al asignar por codigo. */
  lastDrivers?: ReadonlyMap<string, string>;
  onAccept: (trip: ProviderTrip, offerId: number) => Promise<boolean>;
  onReject: (trip: ProviderTrip, offerId: number, reason: OfferRejectReason) => Promise<boolean>;
  onAssign: (trip: ProviderTrip, vehicleId: string, driverId: string) => Promise<boolean>;
  /**
   * PP-41: detalle de un viaje asignado (la hoja de ruta). Lo pasa la seccion
   * Viajes; sin el, el viaje asignado muestra el recorrido de siempre.
   */
  assignedDetail?: AssignedDetail;
}

/** Lo que necesita la hoja de ruta de un viaje asignado, ya con la nomina cargada. */
export type AssignedDetail = (input: {
  trip: ProviderTrip;
  vehicles: RosterState<SiteVehicle>;
  drivers: RosterState<Driver>;
}) => React.ReactNode;

type Panel = "none" | "reject" | "assign" | "route";

/** Menos de 5 minutos es urgente (naranja); los ultimos 15 segundos, critico. */
const URGENT_SECONDS = 300;
const CRITICAL_SECONDS = 15;

function Countdown({ trip, now }: { trip: ProviderTrip; now: number }): React.JSX.Element | null {
  if (trip.offer === undefined) return null;
  const seconds = remainingSeconds(trip.offer.deadlineAt, now);
  const what = trip.offer.state === "offered" ? "responder" : "asignar vehículo y conductor";
  if (seconds === 0) {
    return (
      <p className="m-0 text-xs font-medium leading-tight text-critico-texto md:text-right">
        Tiempo cumplido para {what}. Consultando el estado del viaje...
      </p>
    );
  }
  const tone = seconds <= CRITICAL_SECONDS ? "text-critico-texto" : seconds <= URGENT_SECONDS ? "text-naranja-texto" : "text-texto";
  return (
    <p className="m-0 leading-tight md:text-right">
      <span className="sr-only">
        Quedan {formatRemaining(seconds)} para {what}.
      </span>
      <span aria-hidden="true" className="flex items-baseline gap-1.5 md:flex-col md:items-end md:gap-0">
        <b className={cn("tabular text-base font-semibold", tone)}>{formatRemaining(seconds)}</b>
        <span className={cn("text-eyebrow", seconds <= URGENT_SECONDS ? tone : "text-texto-suave")}>
          para {trip.offer.state === "offered" ? "responder" : "asignar"}
        </span>
      </span>
    </p>
  );
}

function AssignedPair({ trip, vehicles, drivers }: Pick<Props, "trip" | "vehicles" | "drivers">): React.JSX.Element | null {
  if (trip.vehicleId === undefined && trip.driverId === undefined) return null;
  const vehicle = vehicles.estado === "listo" ? vehicles.items.find((v) => v.id === trip.vehicleId) : undefined;
  const driver = drivers.estado === "listo" ? drivers.items.find((d) => d.id === trip.driverId) : undefined;
  return (
    <dl className="m-0 flex flex-wrap gap-x-4 gap-y-1 text-xs">
      <div className="flex flex-wrap items-center gap-1.5">
        <dt className="text-texto-debil">Vehículo</dt>
        <dd className="m-0 flex flex-wrap items-center gap-1.5 text-texto-2">
          <span className="tabular">{vehicle === undefined ? (trip.vehicleId ?? "Sin asignar") : `${vehicle.code} · ${vehicle.plate}`}</span>
          {vehicle !== undefined && <EligibilityBadge eligibility={vehicle.eligibility} />}
        </dd>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <dt className="text-texto-debil">Conductor</dt>
        <dd className="m-0 flex flex-wrap items-center gap-1.5 text-texto-2">
          <span>{driver === undefined ? (trip.driverId ?? "Sin asignar") : `${driver.code} · ${driver.name}`}</span>
          {driver !== undefined && <EligibilityBadge eligibility={driver.eligibility} />}
        </dd>
      </div>
    </dl>
  );
}

function RejectForm({
  busy,
  onConfirm,
  onCancel,
}: {
  busy: boolean;
  onConfirm: (reason: OfferRejectReason) => void;
  onCancel: () => void;
}): React.JSX.Element {
  const id = useId();
  const [reason, setReason] = useState<OfferRejectReason | "">("");
  return (
    <form
      aria-labelledby={`${id}-title`}
      className="flex flex-col gap-3 rounded-md border border-linea bg-hundida p-4"
      onSubmit={(event) => {
        event.preventDefault();
        if (reason !== "") onConfirm(reason);
      }}
    >
      <h3 id={`${id}-title`} className="m-0 text-sm font-semibold text-texto">
        Rechazar la oferta
      </h3>
      <div className="max-w-sm">
        <SelectField
          label="Motivo del rechazo"
          hint="El viaje pasa solo al siguiente proveedor de la planta."
          value={reason}
          disabled={busy}
          onChange={(event) => setReason(event.target.value as OfferRejectReason | "")}
        >
          <option value="">Elegir motivo</option>
          {REJECT_REASONS.map((r) => (
            <option key={r} value={r}>
              {REJECT_REASON_LABEL[r]}
            </option>
          ))}
        </SelectField>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button type="submit" size="sm" disabled={reason === "" || busy}>
          Confirmar rechazo
        </Button>
        <Button variant="secundario" size="sm" onClick={onCancel} disabled={busy}>
          Cancelar
        </Button>
      </div>
    </form>
  );
}

type DetailState = { estado: "cargando" } | { estado: "error" } | { estado: "listo"; data: ProviderTripDetailResponse };

/**
 * Recorrido a pedido: horarios y cantidad de pasajeros por parada, sin
 * identidad de pasajeros. "Copiar datos del viaje" (PP-65) sale del mismo
 * detalle, para cargar el viaje en el sistema propio antes de asignar.
 */
function RouteDetail({
  session,
  port,
  site,
  siteName,
  trip,
}: {
  session: PortalSession;
  port: RequestsPort;
  site: RequestsSite;
  siteName: string;
  trip: ProviderTrip;
}): React.JSX.Element {
  const tripId = trip.tripId;
  const [state, setState] = useState<DetailState>({ estado: "cargando" });
  const { siteId, providerId } = site;
  useEffect(() => {
    let current = true;
    port
      .detail(session, { siteId, providerId }, tripId)
      .then((data) => {
        if (current) setState({ estado: "listo", data });
      })
      .catch(() => {
        if (current) setState({ estado: "error" });
      });
    return () => {
      current = false;
    };
  }, [port, session, siteId, providerId, tripId]);

  if (state.estado === "cargando") return <SkeletonRows rows={3} label="Consultando el recorrido" />;
  if (state.estado === "error") return <Failure title="No se pudo consultar el recorrido.">Reintentar en unos minutos.</Failure>;
  const { stops, timezone } = state.data;
  if (stops.length === 0) {
    return (
      <p role="status" className="m-0 text-sm text-texto-suave">
        El recorrido no está disponible para este viaje.
      </p>
    );
  }
  const names = stopNames(stops, trip.summary?.direction);
  return (
    <div className="flex flex-col gap-3">
      <ol aria-label="Recorrido" className="m-0 flex list-none flex-col p-0 text-sm">
        {stops.map((stop) => (
          <li key={stop.stopId} className="flex min-h-fila flex-wrap items-center gap-x-3 border-t border-divisor first:border-t-0">
            <span className="tabular w-14 font-semibold text-texto">{formatClock(stop.estimatedPassTime, timezone)}</span>
            <span className="text-texto-2">{names.get(stop.stopId) ?? "Parada"}</span>
            {names.get(stop.stopId)?.startsWith("Planta") !== true && (
              <span className="text-texto-suave">
                {stop.passengerCount === 1 ? "1 pasajero" : `${String(stop.passengerCount)} pasajeros`}
              </span>
            )}
          </li>
        ))}
      </ol>
      <p className="m-0 text-xs text-texto-suave">Paradas con cantidad de pasajeros, sin personas. La hoja de ruta con nombres aparece una vez asignado el viaje.</p>
      <DispatchCopy sheet={buildRouteSheet({ detail: state.data, siteName })} />
    </div>
  );
}

function stateDot(trip: ProviderTrip): string {
  if (trip.status === "in_progress") return "bg-ok";
  if (trip.status === "completed" || trip.status === "cancelled") return "bg-texto-debil";
  if (trip.assignmentStatus === "assigned") return "bg-acero-claro";
  if (trip.assignmentStatus === "offered" || trip.assignmentStatus === "accepted") return "bg-ambar";
  return "bg-texto-debil";
}

export function TripCard(props: Props): React.JSX.Element {
  const { trip, now, busy, vehicles, drivers, onAccept, onReject, onAssign } = props;
  const [panel, setPanel] = useState<Panel>("none");
  const titleId = useId();
  const actions = tripActions(trip, now);
  const offerId = trip.offer?.offerId;
  const summary = summaryParts(trip.summary, props.timezone);
  const departure = trip.summary?.departureAt === undefined ? "" : formatClock(trip.summary.departureAt, props.timezone);
  // La hoja de ruta (con pasajeros) es del viaje asignado; la API decide igual que datos trae.
  const sheet = props.assignedDetail !== undefined && trip.assignmentStatus === "assigned";
  const pendingAction = trip.assignmentStatus === "offered" || trip.assignmentStatus === "accepted";

  // Si el viaje deja de admitir la accion abierta (vencio, lo tomo el
  // servidor), el panel se cierra: no se deja un formulario que la API va a
  // rechazar.
  const open: Panel =
    (panel === "reject" && !actions.respond) || (panel === "assign" && !actions.assign && !actions.reassign) ? "none" : panel;

  const toggle = (next: Panel) => setPanel(open === next ? "none" : next);
  const title = tripTitle(trip, props.timezone);

  return (
    <li
      aria-labelledby={titleId}
      className={cn(
        "grid grid-cols-[56px_minmax(0,1fr)] items-center gap-x-3 gap-y-2 border-t border-divisor px-3.5 py-2.5 first:border-t-0 hover:bg-velo md:grid-cols-[64px_minmax(0,1fr)_auto_auto] md:gap-x-3.5",
        !pendingAction && "text-texto-2",
      )}
    >
      <p className={cn("tabular m-0 self-start text-base font-semibold leading-tight", pendingAction ? "text-texto" : "text-texto-2")}>
        {departure === "" ? "--:--" : departure}
      </p>
      <div className="flex min-w-0 flex-col gap-0.5">
        <h2 id={titleId} className="tabular m-0 text-sm font-medium leading-tight text-texto">
          {title}
        </h2>
        <p className="m-0 text-xs text-texto-suave">
          {trip.date !== props.inboxDate && <span className="font-semibold text-ambar">Viaje del {formatDate(trip.date)} · </span>}
          {VEHICLE_TYPE_LABEL[trip.vehicleType]} · {TRIP_STATUS_LABEL[trip.status]}
        </p>
        {summary.length > 0 && (
          <p aria-label="Resumen del viaje" className="tabular m-0 truncate text-xs text-texto-suave">
            {summary.join(" · ")}
          </p>
        )}
        <AssignedPair trip={trip} vehicles={vehicles} drivers={drivers} />
        {trip.assignmentStatus === "assigned" && !actions.reassign && (
          <p className="m-0 text-xs text-texto-debil">El viaje ya no está programado: la asignación no se puede cambiar.</p>
        )}
      </div>
      <div className="col-start-2 flex flex-col items-start gap-1 md:col-start-auto md:min-w-[96px] md:items-end">
        {pendingAction ? (
          <Countdown trip={trip} now={now} />
        ) : (
          <Badge variant={trip.assignmentStatus === "assigned" ? "ok" : "neutro"}>
            <i aria-hidden="true" className={cn("mr-1 inline-block size-1.5 rounded-full", stateDot(trip))} />
            {ASSIGNMENT_STATUS_LABEL[trip.assignmentStatus]}
          </Badge>
        )}
        {pendingAction && <Badge variant="neutro">{ASSIGNMENT_STATUS_LABEL[trip.assignmentStatus]}</Badge>}
      </div>
      <div className="col-start-2 flex flex-wrap gap-1.5 md:col-start-auto">
        {trip.assignmentStatus === "offered" && (
          <>
            <Button
              size="sm"
              disabled={!actions.respond || busy || offerId === undefined}
              onClick={() => {
                if (offerId !== undefined) void onAccept(trip, offerId);
              }}
            >
              Aceptar
            </Button>
            <Button size="sm" variant="secundario" disabled={!actions.respond || busy} aria-expanded={open === "reject"} onClick={() => toggle("reject")}>
              Rechazar
            </Button>
          </>
        )}
        {trip.assignmentStatus === "accepted" && (
          <Button size="sm" disabled={!actions.assign || busy} aria-expanded={open === "assign"} onClick={() => toggle("assign")}>
            Asignar vehículo y conductor
          </Button>
        )}
        {trip.assignmentStatus === "assigned" && (
          <Button size="sm" variant="secundario" disabled={!actions.reassign || busy} aria-expanded={open === "assign"} onClick={() => toggle("assign")}>
            Cambiar asignación
          </Button>
        )}
        <Button size="sm" variant="fantasma" aria-expanded={open === "route"} onClick={() => toggle("route")}>
          {sheet
            ? open === "route" ? "Ocultar hoja de ruta" : "Ver hoja de ruta"
            : open === "route" ? "Ocultar recorrido" : "Ver recorrido"}
        </Button>
      </div>

      {open === "reject" && offerId !== undefined && (
        <div className="col-span-full">
          <RejectForm
            busy={busy}
            onCancel={() => setPanel("none")}
            onConfirm={(reason) => {
              void onReject(trip, offerId, reason).then((done) => {
                if (done) setPanel("none");
              });
            }}
          />
        </div>
      )}
      {open === "assign" && (
        <div className="col-span-full">
          <AssignForm
            trip={trip}
            vehicles={vehicles}
            drivers={drivers}
            busy={busy}
            providerId={props.site.providerId}
            {...(props.lastDrivers !== undefined ? { lastDrivers: props.lastDrivers } : {})}
            onCancel={() => setPanel("none")}
            onSubmit={(vehicleId, driverId) => {
              void onAssign(trip, vehicleId, driverId).then((done) => {
                if (done) setPanel("none");
              });
            }}
          />
        </div>
      )}
      <Sheet open={open === "route"} onClose={() => setPanel("none")} title={sheet ? "Hoja de ruta" : "Recorrido"} subtitle={`${title} · ${props.siteName}`}>
        {open === "route" &&
          (sheet && props.assignedDetail !== undefined
            ? props.assignedDetail({ trip, vehicles, drivers })
            : <RouteDetail session={props.session} port={props.port} site={props.site} siteName={props.siteName} trip={trip} />)}
      </Sheet>
    </li>
  );
}
